import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  redirect,
  redirectDocument,
} from "react-router";
import { marketingRedirect } from "./utils/marketingSites.ts";
import { Partytown } from "@qwik.dev/partytown/react";
import type { PartytownProps } from "@qwik.dev/partytown/react";
import type { Route } from "./+types/root";
import swiperCss from "swiper/css?url";
import swiperPaginationCss from "swiper/css/pagination?url";
import swiperEffectFadeCss from "swiper/css/effect-fade?url";
import swiperFreeModeCss from "swiper/css/free-mode?url";
import appScss from "./assets/scss/index.scss?url";
import tailwindCss from "./assets/tailwind.css?url";
import { restoredSessionScript } from "./restoredSession.ts";

// Also cover SPA navigation, not only initial visits normalized by entry.client.
export const clientMiddleware: Route.ClientMiddlewareFunction[] = [
  async ({ request }, next) => {
    const url = new URL(request.url);
    const destination = marketingRedirect(url);
    if (destination) {
      const target = new URL(destination);
      throw target.origin === url.origin
        ? redirect(`${target.pathname}${target.search}${target.hash}`)
        : redirectDocument(destination);
    }
    await next();
  },
];

/**
 * The global stylesheets, as real `<link>`s in the document head.
 *
 * `?url`, not a side-effect import: React Router's dev critical CSS walks every
 * module a side-effect stylesheet reaches, and Tailwind registers each file it
 * scans for classes as a dependency of `tailwind.css` — so the dev bundle swept
 * in stylesheets this app never loads (the ui package's own demo app sets
 * `body { display: flex }`), then dropped them on hydration and the whole page
 * jumped. `?url` imports are skipped by that walk, and the link is compiled by
 * the same pipeline in dev and build.
 *
 * Then the Fixel weights the shell and page titles are set in — Regular through
 * Black: body text, the side panel (ExtraBold), headings. Discovered only once
 * the stylesheet is parsed, they used to land after first paint and swap in
 * wider than the fallback. Thin, ExtraLight and Light stay lazy: a handful of
 * components use them, and a preload the page never uses is a wasted download.
 */
export const links: Route.LinksFunction = () => [
  ...[
    swiperCss,
    swiperPaginationCss,
    swiperEffectFadeCss,
    swiperFreeModeCss,
    appScss,
    tailwindCss,
  ].map((href) => ({ rel: "stylesheet", href })),
  ...[
    "/fonts/FixelDisplay-400_Regular.woff2",
    "/fonts/FixelDisplay-500_Medium.woff2",
    "/fonts/FixelDisplay-600_SemiBold.woff2",
    "/fonts/FixelDisplay-700_Bold.woff2",
    "/fonts/FixelDisplay-800_ExtraBold.woff2",
    "/fonts/FixelDisplay-900_Black.woff2",
  ].map((href) => ({
    rel: "preload",
    as: "font",
    type: "font/woff2",
    href,
    crossOrigin: "anonymous",
  })),
];

// Inline scripts ported from the old src/index.html <head>/<body>.

/**
 * GTM, run by Partytown in a web worker (`type="text/partytown"`), so it and
 * every tag it loads stay off the main thread. `gtm.js` and `gtag/js` send CORS
 * headers for our origin, which the worker's `fetch` needs. Pushes from the app
 * (`useGTMEvents`) reach it through `<Partytown forward={["dataLayer.push"]}>`.
 */
const gtmHead = `(function (w, d, s, l, i) {
  w[l] = w[l] || [];
  w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  var f = d.getElementsByTagName(s)[0],
    j = d.createElement(s),
    dl = l != "dataLayer" ? "&l=" + l : "";
  j.async = true;
  j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
  f.parentNode.insertBefore(j, f);
})(window, document, "script", "dataLayer", "GTM-TRRR87TV");`;

/**
 * Hosts of scripts GTM's tags load that send no CORS headers, so the worker's
 * `fetch` of them is blocked. Partytown fetches them through `/3p/<host>/…` on
 * the platform API instead, whose nginx (`platform` repo,
 * `docker/nginx/thirdparty-*.conf`) keeps the same allowlist — add a host in
 * both places. Only scripts go through it: pixels and beacons are `no-cors`
 * requests, so they still go straight from the browser, with its own IP.
 *
 * Addressable is one of them: GTM's Addressable tag fires its PageView, so the
 * page no longer loads `p.js` itself (that counted every pageview twice).
 */
const thirdPartyScriptHosts = [
  "connect.facebook.net",
  "static.ads-twitter.com",
  "analytics.tiktok.com",
  "tag.adrsbl.io",
];
const thirdPartyProxy = new URL(
  "/3p/",
  import.meta.env.VITE_REACT_APP_API_BASE_URL
).href;

/**
 * Partytown hands `resolveUrl` to its worker as source text (`String(fn)`), so
 * a closure would lose the host list and the proxy origin: they are spliced
 * into the body instead.
 */
const resolveUrl = new Function(
  "url",
  "location",
  "type",
  `if (type === "script" && ${JSON.stringify(thirdPartyScriptHosts)}.includes(url.hostname)) {
    return new URL(${JSON.stringify(thirdPartyProxy)} + url.hostname + url.pathname + url.search);
  }`
) as NonNullable<PartytownProps["resolveUrl"]>;

const pwaDetect = `var isPWA = navigator?.standalone ||
  window.matchMedia("(display-mode: standalone)").matches || false;
if (isPWA) { localStorage.setItem("_____PWA", document.location.href); }`;

const utmScript = `var value = new URL(location.href).searchParams.get("utm_prid");
if (value) {
  var date = new Date();
  date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000);
  document.cookie = "prop_visitor_id=" + (value || "") + "; expires=" + date.toUTCString() + "; path=/";
}`;

const serviceWorkerScript = `async function unregisterServiceWorkers() {
  if ("serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      // Partytown's worker (scope /~partytown/) is how GTM's web worker talks
      // to the page; without it its requests fall through to the router (405).
      await Promise.all(
        registrations
          .filter((r) => !new URL(r.scope).pathname.startsWith("/~partytown/"))
          .map((r) => r.unregister())
      );
      return true;
    } catch (error) {
      console.error("Failed to unregister service workers:", error);
      return false;
    }
  }
  return false;
}
if ("serviceWorker" in navigator && !window.location.search.includes("recovery")) {
  unregisterServiceWorkers();
}`;

const description =
  "Avalanche's leading Web3 gaming hub. Studio & Publisher empowering and nurturing epic games. The ultimate home for indie game devs building on AVAX.";
const orgImage = "https://static.funtico.com/org_image_funtico.png";

// The document shell. Replaces src/index.html — the React Router <Meta/>/<Links/>
// slots render per-route meta() output and asset links; <Scripts/> injects the
// client bundle (see entry.client.tsx). Static head/body markup is ported 1:1.
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    // The restored-session script below sets an attribute on <html> before
    // hydration; that difference is intended.
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://funtico.com/" />
        <meta property="og:image" content={orgImage} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://funtico.com/" />
        <meta property="twitter:image" content={orgImage} />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon/favicon-16x16.png"
        />
        <link
          rel="mask-icon"
          href="/favicon/safari-pinned-tab.svg"
          color="#5bbad5"
        />
        <link rel="shortcut icon" href="/favicon/favicon.ico" />
        <link rel="dns-prefetch" href="https://api.funtico.com/" />
        <link rel="dns-prefetch" href="https://static.funtico.com/" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />
        <meta name="apple-mobile-web-app-title" content="Funtico" />
        <script dangerouslySetInnerHTML={{ __html: restoredSessionScript }} />
        {/* Serves its worker from `public/~partytown` (`partytown copylib`). */}
        <Partytown forward={["dataLayer.push"]} resolveUrl={resolveUrl} />
        {/* Partytown stamps the script it picks up with `data-ptid`, sometimes
            before hydration; that attribute is its, not a render mismatch. */}
        <script
          type="text/partytown"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: gtmHead }}
        />
        <script type="module" dangerouslySetInnerHTML={{ __html: pwaDetect }} />
        <Meta />
        <Links />
      </head>
      <body>
        <noscript
          dangerouslySetInnerHTML={{
            __html: `<style>#before_load_preloader{display:none !important;}</style><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TRRR87TV" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
          }}
        />
        {children}
        <script dangerouslySetInnerHTML={{ __html: utmScript }} />
        <script dangerouslySetInnerHTML={{ __html: serviceWorkerScript }} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// Root stays minimal so it can render in Node when React Router prerenders the
// SPA fallback (ssr:false). All app-wide providers — which run browser-only code
// (wagmi, gsap, window access) — live in the client-only `providers` layout
// route below, guarded by its HydrateFallback. See adr/023.
export default function Page() {
  return <Outlet />;
}

// SPA mode renders this (not the route tree) into the prerendered fallback HTML,
// so the browser-only provider subtree never executes in Node. It is therefore
// where the preloader belongs: baked into the HTML the browser parses, on screen
// before any script runs, and replaced — by React, when the router has hydrated
// and the initial route's modules have loaded — with the app itself. That is the
// window it exists to cover, and it is wider than DOMContentLoaded, which
// deferred module scripts can beat.
//
// `.preloader.active` is opacity 1 (assets/scss/layout/_preloader.scss); with
// scripts off, nothing replaces this, so the <noscript> rule in <Layout> hides
// it rather than leaving it over a page that will never load.
// HydrateFallback is only permitted on the root route in SPA mode. See adr/023.
export function HydrateFallback() {
  return (
    <div id="before_load_preloader" className="preloader active">
      <div className="fantico_logo">
        <div className="clip_logo"></div>
      </div>
    </div>
  );
}
